import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';

import { ToastrService } from 'ngx-toastr';

import { AppState } from 'src/app/state';
import { DashboardCamelCasePipe } from 'src/app/core/pipes/dashboard-camel-case/dashboard-camel-case.pipe';
import { DashboardSnakeCasePipe } from 'src/app/core/pipes/dashboard-snake-case/dashboard-snake-case.pipe';
import * as sectionActions from './sections.actions';
import * as subsectionActions from './../subsections/subsections.actions';
import { SectionsService } from '../../services/sections/sections.service';
import { Section } from 'src/app/models/section';
import { selectSections, selectSectionsSortingOrder } from './sections.selectors';

export function transformSectionFromApi(section: Section): Section {
  return {
    ...section,
    type: section?.type[0]?.toUpperCase() + section?.type?.slice(1).replace('_', ' '),
  };
}

export function getSectionsSortOrderArray(sections: Section[]): number[] {
  return sections?.map((section) => section.id);
}

@Injectable()
export class SectionsEffects {
  getSections$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sectionActions.getSections),
      switchMap(({ templateId }) => {
        return this.sectionsService.getSections(templateId).pipe(
          map((sections) => {
            const sectionsTransformedFromApi = sections.slice().map((section) => transformSectionFromApi(section));
            const transformedSections = [];
            sectionsTransformedFromApi.forEach((section) => {
              const transformedSection: Section = this.dashboardCamelCasePipe.keysToCamelCase(section);
              transformedSections.push(transformedSection);
            });
            return sectionActions.getSectionsSuccess({ sections: transformedSections });
          }),
          catchError((err: HttpErrorResponse) => of(sectionActions.getSectionsError({ err })))
        );
      })
    )
  );

  getSectionsError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(sectionActions.getSectionsError),
        tap(({ err }) => this.toastrService.error('Unable to load sections'))
      ),
    { dispatch: false }
  );

  getSection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sectionActions.getSection),
      switchMap(({ sectionId }) => {
        return this.sectionsService.getSection(sectionId).pipe(
          map((selectedSection) => {
            const sectionTransformedFromApi = [selectedSection].map((section) => transformSectionFromApi(section));
            const sectionCamelCase = this.dashboardCamelCasePipe.keysToCamelCase(sectionTransformedFromApi[0]);
            return sectionActions.getSectionSuccess({ selectedSection: sectionCamelCase });
          }),
          catchError((err: HttpErrorResponse) => of(sectionActions.getSectionError({ err })))
        );
      })
    )
  );

  getSectionSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sectionActions.getSectionSuccess),
      switchMap(({ selectedSection }) => {
        return [
          selectedSection?.subsections
            ? subsectionActions.getSubsections({
                subsections: selectedSection.subsections,
              })
            : sectionActions.noopAction(),
        ];
      })
    )
  );

  getSectionError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(sectionActions.getSectionError),
        tap(({ err }) => this.toastrService.error('Unable to load section'))
      ),
    { dispatch: false }
  );

  saveSectionsSortingOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sectionActions.saveSectionsSortingOrder),
      switchMap(({ templateId, sortingOrder }) => {
        return this.sectionsService.saveSectionsSortingOrder(templateId, sortingOrder).pipe(
          map((savedSortingOrder) =>
            sectionActions.saveSectionsSortingOrderSuccess({ sortingOrder: savedSortingOrder })
          ),
          catchError((err: HttpErrorResponse) => of(sectionActions.saveSectionsSortingOrderError({ err })))
        );
      })
    )
  );

  createSection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sectionActions.createSection),
      switchMap(({ section }) => {
        const sectionSnakeCase = this.dashboardSnakeCasePipe.keysToSnakeCase(section);
        return this.sectionsService.createSection(sectionSnakeCase).pipe(
          map((createdSection: Section) => {
            const transformedCreatedSection = transformSectionFromApi(createdSection);
            const createdSectionCamelCase = this.dashboardCamelCasePipe.keysToCamelCase(transformedCreatedSection);
            return sectionActions.createSectionSuccess({ createdSection: createdSectionCamelCase });
          }),
          catchError((err: HttpErrorResponse) => {
            return of(sectionActions.createSectionError({ err }));
          })
        );
      })
    )
  );

  createSectionSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sectionActions.createSectionSuccess),
      withLatestFrom(this.store.select(selectSectionsSortingOrder), this.store.select(selectSections)),
      map(([{ createdSection }, sortingOrder, sections]) => {
        this.toastrService.success(`Section ${createdSection?.sectionName} created!`);

        let newSortingOrder;
        if (sortingOrder?.length) {
          newSortingOrder = sortingOrder?.slice();
          newSortingOrder.push(createdSection?.id);
        } else {
          newSortingOrder = getSectionsSortOrderArray(sections);
        }

        return sectionActions.saveSectionsSortingOrder({
          templateId: createdSection?.feedbackTemplateId,
          sortingOrder: newSortingOrder,
        });
      })
    )
  );

  createSectionError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(sectionActions.createSectionError),
        map(({ err }) => {
          this.toastrService.error('Unable to create new section');
        })
      ),
    { dispatch: false }
  );

  updateSection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sectionActions.updateSection),
      switchMap(({ section }) => {
        const sectionSnakeCase = this.dashboardSnakeCasePipe.keysToSnakeCase(section);
        return this.sectionsService.updateSection(sectionSnakeCase).pipe(
          map((updatedSection: Section) => {
            updatedSection = {
              ...updatedSection,
              translations: section.translations,
            };
            const transformedUpdatedSection = transformSectionFromApi(updatedSection);
            const updatedSectionCamelCase = this.dashboardCamelCasePipe.keysToCamelCase(transformedUpdatedSection);
            return sectionActions.updateSectionSuccess({ updatedSection: updatedSectionCamelCase });
          }),
          catchError((err: HttpErrorResponse) => {
            return of(sectionActions.updateSectionError({ err }));
          })
        );
      })
    )
  );

  updateSectionSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(sectionActions.updateSectionSuccess),
        map(({ updatedSection }) => {
          this.toastrService.success(`Section ${updatedSection.sectionName} updated!`);
        })
      ),
    { dispatch: false }
  );

  updateSectionError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(sectionActions.updateSectionError),
        map(({ err }) => {
          this.toastrService.error('Unable to update section');
        })
      ),
    { dispatch: false }
  );

  deleteSection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sectionActions.deleteSection),
      switchMap(({ section }) => {
        const sectionSnakeCase = this.dashboardSnakeCasePipe.keysToSnakeCase(section);
        return this.sectionsService.deleteSection(sectionSnakeCase).pipe(
          map(() => sectionActions.deleteSectionSuccess({ deletedSection: section })),
          catchError((err: HttpErrorResponse) => of(sectionActions.deleteSectionError({ err })))
        );
      })
    )
  );

  deleteSectionSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(sectionActions.deleteSectionSuccess),
        tap(({ deletedSection }) => this.toastrService.success('Section deleted!'))
      ),
    { dispatch: false }
  );

  deleteSectionError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(sectionActions.deleteSectionError),
        tap(({ err }) => this.toastrService.error('Unable to delete section'))
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private sectionsService: SectionsService,
    private dashboardCamelCasePipe: DashboardCamelCasePipe,
    private dashboardSnakeCasePipe: DashboardSnakeCasePipe,
    private toastrService: ToastrService,
    private store: Store<AppState>
  ) {}
}
